/**
 * PeopleGraph — Tree Layout Algorithm
 *
 * Computes node positions and edge connections for the
 * hierarchical org-chart style tree visualization.
 */

import { CATEGORIES } from "./constants";

export function computeTreeLayout(people, canvasWidth) {
  const NODE_W = 160;
  const NODE_H = 72;
  const H_GAP = 32;
  const V_GAP = 80;
  const nodes = [];
  const edges = [];

  // Root node (You)
  const rootX = canvasWidth / 2;
  const rootY = 40;
  nodes.push({
    id: "__root__",
    x: rootX,
    y: rootY,
    w: NODE_W,
    h: NODE_H,
    label: "You",
    type: "root",
  });

  // Group by category
  const grouped = {};
  CATEGORIES.forEach((c) => (grouped[c.id] = []));
  people.forEach((p) => {
    if (grouped[p.category]) grouped[p.category].push(p);
  });

  const activeCategories = CATEGORIES.filter((c) => grouped[c.id].length > 0);
  if (activeCategories.length === 0) return { nodes, edges };

  // Category nodes
  const totalCatWidth =
    activeCategories.length * NODE_W + (activeCategories.length - 1) * H_GAP;
  const catStartX = rootX - totalCatWidth / 2 + NODE_W / 2;
  const catY = rootY + NODE_H + V_GAP;

  activeCategories.forEach((cat, i) => {
    const catX = catStartX + i * (NODE_W + H_GAP);
    const catNodeId = `__cat_${cat.id}__`;
    nodes.push({
      id: catNodeId,
      x: catX,
      y: catY,
      w: NODE_W,
      h: NODE_H,
      label: `${cat.icon} ${cat.label}`,
      type: "category",
      category: cat.id,
      color: cat.color,
      count: grouped[cat.id].length,
    });
    edges.push({ from: "__root__", to: catNodeId });

    // People under this category
    const catPeople = grouped[cat.id];

    // Check for manual links (parentId pointing to another person in this category)
    const childMap = {};
    const topLevel = [];
    catPeople.forEach((p) => {
      if (p.parentId && catPeople.find((pp) => pp.id === p.parentId)) {
        if (!childMap[p.parentId]) childMap[p.parentId] = [];
        childMap[p.parentId].push(p);
      } else {
        topLevel.push(p);
      }
    });

    // Layout top-level people
    const personW = 170;
    const personH = 78;
    const pGap = 20;
    const totalPWidth =
      topLevel.length * personW + (topLevel.length - 1) * pGap;
    const pStartX = catX - totalPWidth / 2 + personW / 2;
    const pY = catY + NODE_H + V_GAP;

    const layoutPerson = (person, px, py, parentNodeId) => {
      nodes.push({
        id: person.id,
        x: px,
        y: py,
        w: personW,
        h: personH,
        label: person.name,
        type: "person",
        person,
        color: cat.color,
      });
      edges.push({ from: parentNodeId, to: person.id });

      // Layout children
      const children = childMap[person.id] || [];
      if (children.length > 0) {
        const cTotalW =
          children.length * personW + (children.length - 1) * pGap;
        const cStartX = px - cTotalW / 2 + personW / 2;
        const cY = py + personH + V_GAP * 0.7;
        children.forEach((child, ci) => {
          layoutPerson(child, cStartX + ci * (personW + pGap), cY, person.id);
        });
      }
    };

    topLevel.forEach((person, pi) => {
      layoutPerson(person, pStartX + pi * (personW + pGap), pY, catNodeId);
    });
  });

  return { nodes, edges };
}
